const CLIENT_CONTEXT_HEADER = 'X-Client-Context';
const NEXT_CONTEXT_HEADER = 'X-Request-Context';
const CONTEXT_STORAGE_KEY = '__ps_rc';
const MAX_COUNTER_VALUE = 255;
const MAX_PAYLOAD_BYTES = 1024;
const MAX_HEADER_LENGTH = 2048;

type BotSignalPoint = 'check-phone' | 'registration' | 'password-login' | 'resend';
type WebGlClass = 'hardware' | 'swiftshader' | 'llvmpipe' | 'unavailable' | 'unknown';

type NavigatorWithDeviceMemory = Navigator & {
    deviceMemory?: number;
};

type BotChallenge = {
    id: string;
    nonce: string;
    seq: number;
    iat: number;
    sig: string;
};

type BehaviorState = {
    firstInteractionMs: number | null;
    pointerEvents: number;
    keyEvents: number;
    touchEvents: number;
    scrollEvents: number;
    focusChanges: number;
    visibilityChanges: number;
    trustedEvents: number;
};

const startedAt = typeof performance === 'undefined' ? 0 : performance.now();
let sequence = 0;
let listenersInstalled = false;
let currentChallenge: BotChallenge | null = null;

const behavior: BehaviorState = {
    firstInteractionMs: null,
    pointerEvents: 0,
    keyEvents: 0,
    touchEvents: 0,
    scrollEvents: 0,
    focusChanges: 0,
    visibilityChanges: 0,
    trustedEvents: 0,
};

const increment = (value: number): number => Math.min(value + 1, MAX_COUNTER_VALUE);

const getAgeMs = (): number => {
    if (typeof performance === 'undefined') {
        return 0;
    }
    return Math.max(0, Math.round(performance.now() - startedAt));
};

const recordInteraction = (event: Event, counter: keyof Pick<BehaviorState, 'pointerEvents' | 'keyEvents' | 'touchEvents' | 'scrollEvents'>): void => {
    behavior[counter] = increment(behavior[counter]);
    if (behavior.firstInteractionMs === null) {
        behavior.firstInteractionMs = getAgeMs();
    }
    if (event.isTrusted) {
        behavior.trustedEvents = increment(behavior.trustedEvents);
    }
};

const installListeners = (): void => {
    if (listenersInstalled || typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    listenersInstalled = true;
    window.addEventListener('pointerdown', (event) => recordInteraction(event, 'pointerEvents'), { passive: true });
    window.addEventListener('keydown', (event) => recordInteraction(event, 'keyEvents'), { passive: true });
    window.addEventListener('touchstart', (event) => recordInteraction(event, 'touchEvents'), { passive: true });
    window.addEventListener('scroll', (event) => recordInteraction(event, 'scrollEvents'), { passive: true });
    window.addEventListener('focus', () => {
        behavior.focusChanges = increment(behavior.focusChanges);
    });
    document.addEventListener('visibilitychange', () => {
        behavior.visibilityChanges = increment(behavior.visibilityChanges);
    });
};

const getWebGlClass = (): WebGlClass => {
    if (typeof document === 'undefined') {
        return 'unavailable';
    }

    try {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!context || !('getExtension' in context) || !('getParameter' in context)) {
            return 'unavailable';
        }

        const debugInfo = context.getExtension('WEBGL_debug_renderer_info') as { UNMASKED_RENDERER_WEBGL?: number } | null;
        if (!debugInfo?.UNMASKED_RENDERER_WEBGL) {
            return 'unknown';
        }

        const renderer = String(context.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)).toLowerCase();
        if (renderer.includes('swiftshader')) {
            return 'swiftshader';
        }
        if (renderer.includes('llvmpipe')) {
            return 'llvmpipe';
        }
        return renderer ? 'hardware' : 'unknown';
    } catch {
        return 'unavailable';
    }
};

const toBase64Url = (bytes: Uint8Array): string => {
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const parseChallenge = (value: string | null): BotChallenge | null => {
    if (!value || value.length > 320) {
        return null;
    }
    const parts = value.split('.');
    if (parts.length !== 5
        || !/^[A-Za-z0-9-]{1,64}$/.test(parts[0])
        || !/^[A-Za-z0-9_-]{16,64}$/.test(parts[1])
        || !/^[A-Za-z0-9_-]{1,128}$/.test(parts[4])) {
        return null;
    }
    const seq = Number(parts[2]);
    if (!Number.isSafeInteger(seq) || seq < 1 || seq > 1_000_000) {
        return null;
    }
    const iat = Number(parts[3]);
    if (!Number.isSafeInteger(iat) || iat <= 0) {
        return null;
    }
    return { id: parts[0], nonce: parts[1], seq, iat, sig: parts[4] };
};

const serializeChallenge = (challenge: BotChallenge): string => (
    `${challenge.id}.${challenge.nonce}.${challenge.seq}.${challenge.iat}.${challenge.sig}`
);

const getChallenge = (): BotChallenge | null => {
    if (currentChallenge) {
        return currentChallenge;
    }
    try {
        currentChallenge = parseChallenge(window.sessionStorage.getItem(CONTEXT_STORAGE_KEY));
    } catch {
        currentChallenge = null;
    }
    return currentChallenge;
};

export const captureBotChallengeFromResponse = (response: Response): void => {
    try {
        const challenge = parseChallenge(response.headers.get(NEXT_CONTEXT_HEADER));
        if (!challenge) {
            return;
        }
        currentChallenge = challenge;
        window.sessionStorage.setItem(CONTEXT_STORAGE_KEY, serializeChallenge(challenge));
    } catch {
        // Telemetry must never affect the form response.
    }
};

export const getBotSignalPoint = (action: string): BotSignalPoint | null => {
    const path = action.split('?')[0];
    if (path.endsWith('/check-phone.action')) {
        return 'check-phone';
    }
    if (path.endsWith('/registration.action')) {
        return 'registration';
    }
    if (path.endsWith('/j_spring_security_check')) {
        return 'password-login';
    }
    if (path.endsWith('/resend.action')) {
        return 'resend';
    }
    return null;
};

export const getBotSignalHeaders = (action: string): Record<string, string> | undefined => {
    const point = getBotSignalPoint(action);
    if (!point || typeof window === 'undefined' || typeof navigator === 'undefined') {
        return undefined;
    }

    try {
        installListeners();
        const challenge = getChallenge();
        sequence = challenge?.seq ?? sequence + 1;

        const navigatorWithMemory = navigator as NavigatorWithDeviceMemory;
        const payload: Record<string, unknown> = {
            v: 2,
            id: challenge?.id ?? null,
            nonce: challenge?.nonce ?? null,
            seq: sequence,
            point,
            environment: {
                wd: navigator.webdriver === true ? 1 : 0,
                dmSupported: 'deviceMemory' in navigator ? 1 : 0,
                dm: navigatorWithMemory.deviceMemory ?? null,
                hw: navigator.hardwareConcurrency ?? null,
                langs: Array.isArray(navigator.languages) ? navigator.languages.length : null,
                plugins: navigator.plugins?.length ?? null,
                outerZero: window.outerWidth === 0 || window.outerHeight === 0 ? 1 : 0,
                tz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
                colorDepth: window.screen?.colorDepth ?? null,
                dpr: window.devicePixelRatio ?? null,
                webgl: getWebGlClass(),
            },
            behavior: {
                ageMs: getAgeMs(),
                ...behavior,
            },
        };
        if (challenge) {
            payload.iat = challenge.iat;
            payload.sig = challenge.sig;
        }
        const bytes = new TextEncoder().encode(JSON.stringify(payload));
        if (bytes.length > MAX_PAYLOAD_BYTES) {
            return undefined;
        }

        const header = `v1.${toBase64Url(bytes)}`;
        if (header.length > MAX_HEADER_LENGTH) {
            return undefined;
        }

        return {
            [CLIENT_CONTEXT_HEADER]: header,
        };
    } catch {
        return undefined;
    }
};

installListeners();
